/**
 * 宮城県の背景ツアー「宮城めぐり」コンテンツ。
 *
 * 軸は北海道・山形と同じく「季節」。5月の松島から始めて、
 * 1年かけて翌年4月の蔵王へ戻る。春=朝 → 夏=昼 → 秋=夕 → 冬=夜。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 秋保大滝を「日本三名瀑」「日本三大瀑布」と断定しない（公式も伝聞形）。
 *  - 仙台城に天守閣を描かない／「青葉城の天守」と書かない。
 *  - 白石城の三階櫓を「現存天守」「国宝」と書かない（平成7年の木造復元）。
 *  - 鳴子峡の赤い橋を名指ししない（公式ページに橋名の記載がない）。
 *  - 田代島の社の正式名称は断定せず「猫神様」と書く。餌やりを勧めない。
 *  - 蔵王エコーラインの冬期通行止めに触れる（冬は行けない）。
 */

import type { StageTour } from '../types';

export const miyagiSceneryTour: StageTour = {
  scenes: [
    {
      id: 'godaido',
      name: '五大堂',
      area: '松島町・5月の朝',
      image: '/assets/scenery/miyagi/scenes/01_godaido.webp',
      hotspots: [
        {
          id: 'godaido-sukashi',
          x: 0.28,
          y: 0.5,
          title: '足もとに波が見える橋',
          body: '島へ渡る橋は「透橋（すかしばし）」。橋桁のあいだが空いていて、上から下の波が透けて見えるので、この名があります。',
          source: 'https://www.town.miyagi-matsushima.lg.jp/page/1199.html',
          mapQuery: '五大堂 松島',
        },
        {
          id: 'godaido-dou',
          x: 0.68,
          y: 0.26,
          title: '政宗が建て直した桃山のお堂',
          body: '大同2年（807年）に坂上田村麻呂が毘沙門堂を建てたのが始まりで、のちに慈覚大師が五大明王像を安置して五大堂と呼ばれるようになりました。いまの建物は伊達政宗が再建したもので、国の重要文化財です。',
          source: 'https://www.town.miyagi-matsushima.lg.jp/page/1199.html',
          mapQuery: '松島 五大堂',
        },
      ],
    },
    {
      id: 'zuiganji',
      name: '瑞巌寺',
      area: '松島町・6月の午前',
      image: '/assets/scenery/miyagi/scenes/02_zuiganji.webp',
      hotspots: [
        {
          id: 'zuiganji-hondou',
          x: 0.54,
          y: 0.32,
          title: '1609年に上棟した本堂',
          body: '伊達政宗は慶長9年（1604年）に寺を造るための縄張りを行い、慶長14年（1609年）に本堂の上棟式が行われました。その前年の1608年に、寺の名を「瑞巌寺」へ改めています。本堂は昭和28年に国宝に指定されました。',
          source: 'https://www.zuiganji.or.jp/history/',
          mapQuery: '瑞巌寺',
        },
        {
          id: 'zuiganji-engi',
          x: 0.24,
          y: 0.4,
          title: '1200年前にさかのぼる',
          body: '前身は天長5年（828年）に慈覚大師円仁が開いた天台宗の延福寺。1259年ごろに臨済宗へ改まりました。',
          source: 'https://www.zuiganji.or.jp/history/',
          mapQuery: '松島 瑞巌寺',
        },
        {
          id: 'zuiganji-kuri',
          x: 0.68,
          y: 0.34,
          title: '庫裏も国宝',
          body: '台所の役割をになう庫裏は、昭和34年（1959年）に国宝に指定されています。大屋根の上には入母屋造の煙出しがのり、正面上部には豪壮な唐草の彫刻が飾られています。',
          source: 'https://www.zuiganji.or.jp/guide/keidai_02.php',
          mapQuery: '瑞巌寺 庫裏',
        },
      ],
    },
    {
      id: 'misaki',
      name: '御崎',
      area: '気仙沼市・7月の昼',
      image: '/assets/scenery/miyagi/scenes/03_misaki.webp',
      hotspots: [
        {
          id: 'misaki-senjojiki',
          x: 0.44,
          y: 0.56,
          title: '黒い岩の千畳敷',
          body: '幅30メートル、長さ50メートルほどの黒色粘板岩の平らな岩場に、太平洋の波が直接打ち寄せます。「千畳敷」とも呼ばれています。',
          source:
            'https://kesennuma-kanko.jp/%e5%be%a1%e5%b4%8e%ef%bc%88%e5%85%ab%e9%9a%bb%e6%9b%b3%ef%bc%89/',
          relatedItemId: 'fukahire',
          mapQuery: '御崎 気仙沼',
        },
        {
          id: 'misaki-hassoubiki',
          x: 0.24,
          y: 0.5,
          title: '「八隻曳」という岩場',
          body: '八隻曳は、御崎神社の神さまが八隻の船で上陸したと伝えられる岩場です。御崎は唐桑半島のいちばん南の端にあり、太平洋の荒波が打ち寄せます。',
          source:
            'https://kesennuma-kanko.jp/%e5%be%a1%e5%b4%8e%ef%bc%88%e5%85%ab%e9%9a%bb%e6%9b%b3%ef%bc%89/',
          mapQuery: '唐桑半島 御崎神社',
        },
      ],
    },
    {
      id: 'tashirojima',
      name: '田代島',
      area: '石巻市・8月の昼',
      image: '/assets/scenery/miyagi/scenes/04_tashirojima.webp',
      hotspots: [
        {
          id: 'tashirojima-mayu',
          x: 0.62,
          y: 0.36,
          title: 'ネズミから繭を守った猫',
          body: '田代島では昔、繭の生産が行われていて、ネズミから繭を守るために猫が飼われ、大事にされてきました。',
          source: 'https://www.city.ishinomaki.lg.jp/cont/10053500/0050/3639/3639.html',
          mapQuery: '田代島',
        },
        {
          id: 'tashirojima-nekogami',
          x: 0.24,
          y: 0.28,
          title: '猫神様',
          body: '大網漁の網を据えるために岩を崩していたところ、その岩が猫に当たって死なせてしまい、総元締めがねんごろに葬ったのが猫神様の由来と伝わります。漁師たちは、猫のしぐさから天気や漁のようすを読んでいたそうです。',
          source: 'https://www.city.ishinomaki.lg.jp/cont/10053500/0050/3639/3639.html',
          mapQuery: '田代島 猫神社',
        },
        {
          id: 'tashirojima-shima',
          x: 0.42,
          y: 0.6,
          title: '河口から17キロの小さな島',
          body: '旧北上川の河口から東南へおよそ17キロ。面積2.92平方キロ、周囲11.5キロの島です。渡るには網地島ラインを使います。',
          source: 'https://www.city.ishinomaki.lg.jp/cont/10053500/0050/3639/3639.html',
          mapQuery: '網地島ライン 田代島',
        },
      ],
    },
    {
      id: 'akiu-otaki',
      name: '秋保大滝',
      area: '仙台市・9月の午後',
      image: '/assets/scenery/miyagi/scenes/05_akiu_otaki.webp',
      hotspots: [
        {
          id: 'akiu-taki',
          x: 0.5,
          y: 0.34,
          title: '落差55メートルを一気に',
          body: '幅6メートル、落差55メートル。途中で段にならず、まっすぐ落ちる滝です。国の名勝に指定され、「日本の滝百選」に選ばれています。',
          source: 'https://www.sentabi.jp/guidebook/attractions/116/',
          mapQuery: '秋保大滝',
        },
        {
          id: 'akiu-tsubo',
          x: 0.33,
          y: 0.66,
          title: '滝つぼまで5分',
          body: '滝見台から全体を眺められるほか、不動滝橋の脇の遊歩道から5〜10分で滝つぼまで降りられます。紅葉は10月中旬から11月上旬ごろ。',
          source: 'https://www.sentabi.jp/guidebook/attractions/116/',
          mapQuery: '秋保大滝不動尊',
        },
      ],
    },
    {
      id: 'narukokyo',
      name: '鳴子峡',
      area: '大崎市・10月の夕',
      image: '/assets/scenery/miyagi/scenes/06_narukokyo.webp',
      hotspots: [
        {
          id: 'narukokyo-tani',
          x: 0.5,
          y: 0.4,
          title: '深さ100メートルの谷',
          body: '大谷川が刻んだ、深さ100メートルにおよぶ大峡谷。秋には谷が赤や黄色の紅葉に染まります。',
          source: 'https://www.city.osaki.miyagi.jp/kanko/sizen/4/6032.html',
          mapQuery: '鳴子峡',
        },
        {
          id: 'narukokyo-kouyou',
          x: 0.73,
          y: 0.26,
          title: '紅葉と常緑樹が同じ斜面に',
          body: 'ブナ・ナラ・カエデの赤や黄と、マツなど常緑樹の緑が同じ斜面に並びます。見ごろは10月下旬から11月上旬ごろ。',
          source: 'https://www.miyagi-kankou.or.jp/theme/detail.php?id=9805',
          mapQuery: '鳴子峡 紅葉',
        },
        {
          id: 'narukokyo-yuhodo',
          x: 0.23,
          y: 0.56,
          title: '遊歩道は区間も時間も決まっている',
          body: '通年で歩けるわけではありません。中山平側は回顧橋まで片道およそ350メートル、鳴子側はおよそ230メートル地点まで。時間は9時から16時、期間は4月下旬から11月下旬で、天候によって閉まることがあります。',
          source: 'https://www.city.osaki.miyagi.jp/kanko/sizen/4/6032.html',
          mapQuery: '鳴子峡レストハウス',
        },
      ],
    },
    {
      id: 'sendaijo',
      name: '仙台城跡',
      area: '仙台市・11月の夕',
      image: '/assets/scenery/miyagi/scenes/07_sendaijo.webp',
      hotspots: [
        {
          id: 'sendaijo-kiba',
          x: 0.55,
          y: 0.3,
          title: '伊達政宗公騎馬像',
          body: '本丸跡に立つ騎馬像です。日没から23時まで、石垣とともにライトアップされます。',
          source: 'https://www.sentabi.jp/spots/60',
          relatedItemId: 'gyutan-teishoku',
          mapQuery: '仙台城跡',
        },
        {
          id: 'sendaijo-tenshu',
          x: 0.34,
          y: 0.38,
          title: '天守閣は、はじめから無い',
          body: '伊達62万石の居城ですが、将軍家康の警戒を避けるため、あえて天守閣は設けなかったといわれています。探しても見つからないのはそのためです。',
          source: 'https://www.sentabi.jp/spots/60',
          mapQuery: '青葉城址',
        },
        {
          id: 'sendaijo-yougai',
          x: 0.74,
          y: 0.52,
          title: '東と南は断崖',
          body: '標高はおよそ130メートル。東と南を断崖が固める天然の要害でした。平成15年の夏に国の史跡に指定されています。',
          source: 'https://www.sentabi.jp/spots/60',
          mapQuery: '仙台城 本丸跡',
        },
      ],
    },
    {
      id: 'shiroishijo',
      name: '白石城',
      area: '白石市・12月の午後',
      image: '/assets/scenery/miyagi/scenes/08_shiroishijo.webp',
      hotspots: [
        {
          id: 'shiroishijo-yagura',
          x: 0.52,
          y: 0.3,
          title: '「天守」ではなく三階櫓',
          body: '三階三重の櫓で、平成7年3月に木造で復元されました。明治7年の取り壊しから120年ぶりの再建で、石垣の上から16.7メートル。戦後の木造復元では高さも広さも日本最大級です。',
          source: 'https://www.shiro-f.jp/shiroishijo/shiroishijo/donjon',
          relatedItemId: 'shiroishi-umen',
          mapQuery: '白石城',
        },
        {
          id: 'shiroishijo-katakura',
          x: 0.36,
          y: 0.5,
          title: '片倉家の城',
          body: '慶長7年（1602年）に仙台城の支城として片倉小十郎景綱が城主となり、以後代々片倉家の居城でした。元和元年の一国一城令のときも、仙台藩は仙台城と白石城の二つを許されています。',
          source: 'https://www.shiro-f.jp/shiroishijo/shiroishijo/history',
          mapQuery: '白石城 歴史探訪ミュージアム',
        },
      ],
    },
    {
      id: 'osaki-hachimangu',
      name: '大崎八幡宮',
      area: '仙台市・1月の夜',
      image: '/assets/scenery/miyagi/scenes/09_osaki_hachimangu.webp',
      hotspots: [
        {
          id: 'osaki-shaden',
          x: 0.5,
          y: 0.34,
          title: '漆塗りに彩色と彫刻',
          body: '本殿と拝殿を「石の間」でつなぐ権現造。内も外も漆塗りで、そこへ胡粉の彩色、彫刻、金具の装飾が重なります。正面には千鳥破風、向拝には軒唐破風。',
          source: 'https://www.pref.miyagi.jp/soshiki/bunkazai/01oosaki1.html',
          mapQuery: '大崎八幡宮',
        },
        {
          id: 'osaki-kokuhou',
          x: 0.74,
          y: 0.5,
          title: '昭和27年に国宝',
          body: '伊達政宗が造営し、慶長12年（1607年）ごろに完成しました。豊国廟の建築を模したと伝えられ、昭和27年（1952年）11月22日に国宝に指定されています。',
          source: 'https://online.bunka.go.jp/heritages/detail/184266',
          mapQuery: '大崎八幡宮 社殿',
        },
      ],
    },
    {
      id: 'zao-okama',
      name: '蔵王のお釜',
      area: '蔵王町・4月下旬の朝',
      image: '/assets/scenery/miyagi/scenes/10_zao_okama.webp',
      hotspots: [
        {
          id: 'okama-kako',
          x: 0.5,
          y: 0.46,
          title: '三つの峰に囲まれた火口湖',
          body: '刈田岳・熊野岳・五色岳に囲まれた火口湖です。昭和43年の測深では東西・南北とも325メートル、いちばん深いところで27.6メートル、周囲1,080メートルでした。',
          source: 'https://www.zao-machi.com/sightseeing_spot/1.html',
          mapQuery: '蔵王 御釜',
        },
        {
          id: 'okama-goshiki',
          x: 0.64,
          y: 0.44,
          title: '五色湖とも呼ばれる',
          body: '太陽の当たり方で色が変わるので「五色湖」とも呼ばれます。湖水は強い酸性で、生き物は棲んでいません。',
          source: 'https://www.zao-machi.com/sightseeing_spot/1.html',
          mapQuery: '蔵王 五色岳',
        },
        {
          id: 'okama-echoline',
          x: 0.24,
          y: 0.6,
          title: '道が開くのは春',
          body: '蔵王エコーラインと蔵王ハイラインは、毎年11月初旬から4月下旬まで冬期通行止めです。そのあいだは、お釜を見に行くことができません。',
          source: 'https://www.zao-machi.com/sightseeing_spot/1.html',
          mapQuery: '蔵王エコーライン',
        },
      ],
    },
  ],
};
